import React, { useState, useEffect, useCallback } from 'react';
import { FaGasPump, FaTimes, FaSave, FaEdit, FaTrash, FaDollarSign, FaPercent } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fuelService } from '../../services/refuelService';
import { FuelStation, Fuel, FuelRequest, FuelType } from '../../types';

interface AdminFuelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  station: FuelStation;
}

// Helper para obter nome amigável do tipo de combustível
const getFuelTypeName = (type: FuelType): string => {
  const names: Record<FuelType, string> = {
    [FuelType.GASOLINA_COMUM]: 'Gasolina Comum',
    [FuelType.GASOLINA_ADITIVADA]: 'Gasolina Aditivada',
    [FuelType.ETANOL]: 'Etanol',
    [FuelType.DIESEL]: 'Diesel',
    [FuelType.DIESEL_S10]: 'Diesel S10'
  };
  return names[type] || type;
};

const AdminFuelModal: React.FC<AdminFuelModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  station
}) => {
  const [fuels, setFuels] = useState<Fuel[]>([]);
  const [isLoadingFuels, setIsLoadingFuels] = useState(false);
  const [editingFuel, setEditingFuel] = useState<Fuel | null>(null);
  const [formData, setFormData] = useState<FuelRequest>({
    tipoCombustivel: FuelType.GASOLINA_COMUM,
    precoAtual: 0,
    descontoReais: 0,
    percentualCashback: 0,
    percentualLucro: 0,
    litrosMinimosDesconto: 0,
    descontoPorLitro: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadFuels = useCallback(async () => {
    setIsLoadingFuels(true);
    try {
      const response = await fuelService.getFuelsByStation(station.id);
      if (response.success && response.data) {
        setFuels(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar combustíveis:', error);
      toast.error('Erro ao carregar combustíveis');
    } finally {
      setIsLoadingFuels(false);
    }
  }, [station.id]);

  useEffect(() => {
    if (isOpen && station) {
      loadFuels();
    }
  }, [isOpen, station, loadFuels]);

  const handleEditFuel = (fuel: Fuel) => {
    setEditingFuel(fuel);
    setFormData({
      tipoCombustivel: fuel.tipoCombustivel,
      precoAtual: fuel.precoAtual,
      descontoReais: fuel.descontoReais,
      percentualCashback: fuel.percentualCashback || 0,
      percentualLucro: fuel.percentualLucro || 0,
      litrosMinimosDesconto: fuel.litrosMinimosDesconto || 0,
      descontoPorLitro: fuel.descontoPorLitro || 0
    });
  };

  const getInitialFuelFormData = (): FuelRequest => ({
    tipoCombustivel: FuelType.GASOLINA_COMUM,
    precoAtual: 0,
    descontoReais: 0,
    percentualCashback: 0,
    percentualLucro: 0,
    litrosMinimosDesconto: 0,
    descontoPorLitro: 0
  });

  const handleCancelEdit = () => {
    setEditingFuel(null);
    setFormData(getInitialFuelFormData());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingFuel) {
        // Editar combustível existente
        const response = await fuelService.updateFuel(editingFuel.id, formData, station.id);
        if (response.success) {
          toast.success('Combustível atualizado com sucesso!');
          handleCancelEdit();
          loadFuels();
          onSuccess();
        }
      } else {
        // Criar novo combustível
        const response = await fuelService.createFuel(station.id, formData);
        if (response.success) {
          toast.success('Combustível cadastrado com sucesso!');
          setFormData(getInitialFuelFormData());
          loadFuels();
          onSuccess();
        }
      }
    } catch (error: any) {
      console.error('Erro ao salvar combustível:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar combustível');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (fuelId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este combustível?')) {
      return;
    }

    try {
      const response = await fuelService.deleteFuel(fuelId);
      if (response.success) {
        toast.success('Combustível excluído com sucesso!');
        loadFuels();
        onSuccess();
      }
    } catch (error: any) {
      console.error('Erro ao excluir combustível:', error);
      toast.error(error.response?.data?.message || 'Erro ao excluir combustível');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-[#1a2332] to-[#253547] border border-white/10 rounded-2xl p-8 max-w-4xl w-full my-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <FaGasPump className="text-[#089453]" />
            Combustíveis - {station.nomePosto}
          </h2>
          <button
            onClick={onClose}
            className="text-[#888] hover:text-white transition-colors"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Formulário de Adicionar/Editar Combustível */}
        <form onSubmit={handleSubmit} className="mb-8 p-6 bg-[#0f1419]/50 rounded-xl border border-white/5">
          <h3 className="text-lg font-bold text-white mb-4">
            {editingFuel ? 'Editar Combustível' : 'Adicionar Combustível'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Tipo de Combustível */}
            <div>
              <label className="block text-[#888] text-sm font-medium mb-2">
                Tipo de Combustível *
              </label>
              <select
                required
                value={formData.tipoCombustivel}
                onChange={(e) => setFormData({ ...formData, tipoCombustivel: e.target.value as FuelType })}
                className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                disabled={!!editingFuel}
              >
                <option value={FuelType.GASOLINA_COMUM}>Gasolina Comum</option>
                <option value={FuelType.GASOLINA_ADITIVADA}>Gasolina Aditivada</option>
                <option value={FuelType.ETANOL}>Etanol</option>
                <option value={FuelType.DIESEL}>Diesel</option>
                <option value={FuelType.DIESEL_S10}>Diesel S10</option>
              </select>
            </div>

            {/* Preço Atual */}
            <div>
              <label className="block text-[#888] text-sm font-medium mb-2">
                <FaDollarSign className="inline mr-1" />
                Preço Atual (R$) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={formData.precoAtual}
                onChange={(e) => setFormData({ ...formData, precoAtual: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                placeholder="5.99"
              />
            </div>

            {/* Desconto em Reais */}
            <div>
              <label className="block text-[#888] text-sm font-medium mb-2">
                <FaDollarSign className="inline mr-1" />
                Desconto (R$)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.descontoReais}
                onChange={(e) => setFormData({ ...formData, descontoReais: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                placeholder="0.20"
              />
            </div>

            <div>
              <label className="block text-[#888] text-sm font-medium mb-2">
                <FaPercent className="inline mr-1" />
                Cashback (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.percentualCashback}
                onChange={(e) => setFormData({ ...formData, percentualCashback: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                placeholder="5.00"
              />
            </div>

            <div>
              <label className="block text-[#888] text-sm font-medium mb-2">
                <FaPercent className="inline mr-1" />
                Desconto por Litro (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.descontoPorLitro}
                onChange={(e) => setFormData({ ...formData, descontoPorLitro: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                placeholder="2.50"
              />
            </div>

            <div>
              <label className="block text-[#888] text-sm font-medium mb-2">
                <FaPercent className="inline mr-1" />
                Lucro da Plataforma (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.percentualLucro}
                onChange={(e) => setFormData({ ...formData, percentualLucro: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                placeholder="8.00"
              />
            </div>

            <div>
              <label className="block text-[#888] text-sm font-medium mb-2">
                Litros Mínimos para Desconto
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.litrosMinimosDesconto}
                onChange={(e) => setFormData({ ...formData, litrosMinimosDesconto: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                placeholder="30"
              />
            </div>
          </div>

          <div className="flex gap-4">
            {editingFuel && (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-6 py-3 bg-[#0f1419] border border-white/10 text-white rounded-xl hover:bg-white/5 transition-all duration-300"
              >
                Cancelar Edição
              </button>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-[#089453] to-[#84cc34] text-white font-medium rounded-xl hover:shadow-lg hover:shadow-[#089453]/30 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <FaSave />
                  {editingFuel ? 'Atualizar' : 'Adicionar'}
                </>
              )}
            </button>
          </div>
        </form>

        {/* Lista de Combustíveis Cadastrados */}
        <div>
          <h3 className="text-lg font-bold text-white mb-4">Combustíveis Cadastrados</h3>
          {isLoadingFuels ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 border-4 border-[#089453]/10 border-t-[#089453] rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[#888]">Carregando combustíveis...</p>
            </div>
          ) : fuels.length > 0 ? (
            <div className="space-y-3">
              {fuels.map((fuel) => (
                <div
                  key={fuel.id}
                  className="p-4 bg-[#0f1419]/50 rounded-xl border border-white/5 hover:border-[#089453]/30 transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="text-white font-medium">{getFuelTypeName(fuel.tipoCombustivel)}</h4>
                      <div className="flex gap-4 mt-2">
                        <p className="text-sm text-[#888]">
                          Preço: <span className="text-[#84cc34] font-bold">R$ {fuel.precoAtual.toFixed(2)}</span>
                        </p>
                        {fuel.descontoReais > 0 && (
                          <p className="text-sm text-[#888]">
                            Desconto: <span className="text-[#ff9800] font-bold">R$ {fuel.descontoReais.toFixed(2)}</span>
                          </p>
                        )}
                        {(fuel.percentualCashback || 0) > 0 && (
                          <p className="text-sm text-[#888]">
                            Cashback: <span className="text-[#84cc34] font-bold">{fuel.percentualCashback}%</span>
                          </p>
                        )}
                        {(fuel.descontoPorLitro || 0) > 0 && (
                          <p className="text-sm text-[#888]">
                            Desconto/L: <span className="text-[#ff9800] font-bold">{fuel.descontoPorLitro}%</span>
                          </p>
                        )}
                        {(fuel.percentualLucro || 0) > 0 && (
                          <p className="text-sm text-[#888]">
                            Lucro: <span className="text-[#e3f2fd] font-bold">{fuel.percentualLucro}%</span>
                          </p>
                        )}
                        {(fuel.litrosMinimosDesconto || 0) > 0 && (
                          <p className="text-sm text-[#888]">
                            Mínimo: <span className="text-white font-bold">{fuel.litrosMinimosDesconto} L</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditFuel(fuel)}
                        className="px-4 py-2 bg-gradient-to-r from-[#089453] to-[#84cc34] text-white rounded-lg hover:shadow-lg hover:shadow-[#089453]/30 transition-all duration-300 flex items-center gap-2"
                      >
                        <FaEdit />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(fuel.id)}
                        className="px-4 py-2 bg-gradient-to-r from-[#cc0000] to-[#ff3333] text-white rounded-lg hover:shadow-lg hover:shadow-[#cc0000]/30 transition-all duration-300"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center bg-[#0f1419]/50 rounded-xl border border-white/5">
              <FaGasPump className="text-4xl text-[#888] mx-auto mb-3 opacity-50" />
              <p className="text-[#888]">Nenhum combustível cadastrado para este posto</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminFuelModal;
